import { useEffect, useState } from 'react';

import { CommonFlex, CommonText } from '../../../styledComponents/common';
import {
  AbsoluteScaleContainer,
  Bar,
  CenteredText,
  ChartContainer,
  HorizontalScale,
  LegendColor,
  LegendContainer,
  LegendItem,
  LegendText,
  ScrollableContainer,
  VerticalScale,
} from './BarChartStyles';

/**
 * A reusable bar chart component that can display multiple data series
 * @param {Object} props - Component props
 * @param {Array} props.data - An array of objects, each with an id, label, and data series
 * @param {Array} props.series - An array of objects defining the data series to display
 * @param {string} props.xAxisLabel - Label for the X axis
 * @param {number} props.height - Height of the chart in pixels
 * @param {boolean} props.showLegend - Whether to show the legend
 * @param {number} props.maxValue - Maximum value for Y axis (optional, auto-calculated if not provided)
 */
const BarChart = ({
  data = [],
  series = [],
  xAxisLabel = '',
  height = 300,
  showLegend = true,
  maxValue: propMaxValue,
}) => {
  const [maxValue, setMaxValue] = useState(propMaxValue || 100);
  const [tooltipData, setTooltipData] = useState({ text: '', x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const [containerWidth, setContainerWidth] = useState(window.innerWidth - 100);
  // Calculate maximum value if not provided via props
  useEffect(() => {
    if (propMaxValue) {
      setMaxValue(Math.ceil(propMaxValue / 10) * 10);
    } else if (data.length > 0 && series.length > 0) {
      let calculatedMax = 0;

      data.forEach(item => {
        // Calculate the sum of all values for this item
        let itemTotal = 0;
        series.forEach(s => {
          const value = parseFloat(item[s.key]) || 0;
          itemTotal += value;
          if (value > calculatedMax) {
            calculatedMax = value;
          }
        });

        if (itemTotal > calculatedMax) {
          calculatedMax = itemTotal;
        }
      });

      setMaxValue(
        Math.ceil((calculatedMax > 0 ? calculatedMax : 100) / 10) * 10,
      );
    }
  }, [data, series, propMaxValue]);

  // Update container width on window resize
  useEffect(() => {
    const handleResize = () => {
      const chartContainer = document.querySelector('.chart-container');
      if (chartContainer) {
        setContainerWidth(chartContainer.offsetWidth - 80);
      }
    };

    handleResize(); // Call once on init
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // Create tooltip content for display
  const createTooltipContent = (item, seriesKey, seriesName) => {
    const value = item[seriesKey] || 0;
    return `${item.label}: ${seriesName} - ${value}`;
  }; // Handle mouse events for tooltip
  const handleMouseEnter = (event, item, seriesKey, seriesName) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const chartContainer = document.querySelector('.chart-container');
    const chartRect = chartContainer.getBoundingClientRect();

    const text = createTooltipContent(item, seriesKey, seriesName);

    // Position tooltip centered above the bar, relative to chart container
    const x = rect.left - chartRect.left + rect.width / 2;
    const y = rect.top - chartRect.top - 5;

    setTooltipData({ text, x, y });
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };
  return (
    <ChartContainer
      className="chart-container"
      style={{ minHeight: `${height}px` }}
    >
      {' '}
      {showTooltip && (
        <div
          style={{
            position: 'absolute',
            left: `${tooltipData.x}px`,
            top: `${tooltipData.y}px`,
            backgroundColor: '#1f2939',
            color: 'white',
            padding: '7px 15px',
            borderRadius: '4px',
            fontSize: '12px',
            zIndex: 1000,
            pointerEvents: 'none',
            transform: 'translate(-50%, -100%)',
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
            opacity: 0.95,
          }}
        >
          {tooltipData.text}
        </div>
      )}
      {showLegend && (
        <LegendContainer>
          {series.map(s => (
            <LegendItem key={s.key}>
              <LegendColor color={s.color} />
              <LegendText>{s.name}</LegendText>
            </LegendItem>
          ))}
        </LegendContainer>
      )}
      <ScrollableContainer>
        <CommonFlex
          gap="20px"
          alignItems="flex-end"
          justifyContent="start"
          style={{ height: '100%' }}
        >
          <CommonFlex direction="column" gap="20px" alignItems="flex-start">
            {Array.from({ length: 11 }, (_, index) => {
              const v = (maxValue * (10 - index)) / 10;
              return <VerticalScale key={v}>{v}</VerticalScale>;
            })}
          </CommonFlex>
          <CommonFlex
            style={{
              height: 'calc(100% - 24px)',
              marginTop: '11px',
              zIndex: 2,
              position: 'relative',
              width: '100%',
              padding: '0',
              alignItems: 'flex-end',
            }}
            justifyContent="flex-start"
          >
            {data.map(item => (
              <CommonFlex
                direction="column"
                alignItems="bottom"
                justifyContent="flex-end"
                key={item.id}
                style={{
                  height: '100%',
                  position: 'relative',
                  marginRight: '15px',
                  marginBottom: '15px',
                }}
                gap="0"
              >
                <CommonText
                  name
                  fontSize="12px"
                  style={{
                    position: 'absolute',
                    bottom: '-20px',
                    width: '100%',
                    textAlign: 'center',
                  }}
                >
                  {item.label}
                </CommonText>{' '}
                <CommonFlex
                  gap="0px"
                  style={{ height: '100%' }}
                  alignItems="end"
                  direction="column"
                >
                  {' '}
                  {series.map(s => {
                    const value = item[s.key] || 0;
                    // Only render bars for non-zero values
                    if (value <= 0) return null;

                    const heightPercentage = (100 * value) / maxValue;
                    // Ensure a minimum visible height for small values (min 3px)
                    const barHeight = Math.max(heightPercentage * 4.5, 3);

                    return (
                      <Bar
                        key={`${item.id}-${s.key}`}
                        bgColor={s.color}
                        height={`${barHeight}px`}
                        onMouseEnter={e =>
                          handleMouseEnter(e, item, s.key, s.name)
                        }
                        onMouseLeave={handleMouseLeave}
                      />
                    );
                  })}
                </CommonFlex>
              </CommonFlex>
            ))}
          </CommonFlex>
        </CommonFlex>
        <AbsoluteScaleContainer direction="column" gap="20px">
          {' '}
          {Array.from({ length: 11 }, (_, index) => {
            const v = (5 - index) * (maxValue / 5);
            return (
              <HorizontalScale
                key={v}
                width={
                  data?.length > 0
                    ? `${Math.max(50 * data.length, containerWidth)}px`
                    : '100%'
                }
              />
            );
          })}
        </AbsoluteScaleContainer>
      </ScrollableContainer>
      {xAxisLabel && <CenteredText>{xAxisLabel}</CenteredText>}
    </ChartContainer>
  );
};

export default BarChart;
